import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  LayoutDashboard, PenTool, CheckSquare, ShieldCheck, AlertOctagon, FileText,
  AlertTriangle, GitCompare, History, MessageSquare, FileSpreadsheet, Send,
  CheckCircle2, XCircle, RefreshCw, Lock, ArrowRight, Download,
} from "lucide-react";

const NAVY = "hsl(217 63% 15%)";
const MAKER = ["platform_admin", "tax_admin", "tax_manager", "supervisor", "vat_specialist"];
const REVIEW = ["platform_admin", "tax_admin", "tax_manager", "supervisor", "quality_reviewer", "management"];
const APPROVE = ["platform_admin", "tax_admin", "tax_manager", "management"];
const CLIENT = ["client_admin", "client_user"];

const ENV = "demo"; // Gate 9C-A: demo-only
const kapi = () => {
  const h = { "X-Environment": ENV };
  return {
    get: (u, c = {}) => api.get(u, { ...c, headers: { ...(c.headers || {}), ...h } }),
    post: (u, b, c = {}) => api.post(u, b, { ...c, headers: { ...(c.headers || {}), ...h } }),
  };
};

const SMETA = {
  draft: { ar: "مسودّة", en: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  technical_review: { ar: "قيد المراجعة الفنية", en: "Technical Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  reviewed: { ar: "تمت المراجعة", en: "Reviewed", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { ar: "معتمد نهائياً", en: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { ar: "مرفوض", en: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  revision: { ar: "يلزم التعديل", en: "Revision", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  escalated: { ar: "مُصعَّد", en: "Escalated", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

const Card = ({ children, className = "", ...r }) => (
  <div className={`bg-white rounded-xl border border-border p-4 ${className}`} {...r}>{children}</div>
);
const Chip = ({ status }) => {
  const { lang } = useI18n();
  const m = SMETA[status] || SMETA.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${m.cls}`} data-testid={`aiw-status-${status}`}>{lang === "ar" ? m.ar : m.en}</span>;
};

function DemoBanner({ L }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-900 rounded-lg py-1.5 px-4 text-xs font-semibold flex items-center gap-2" data-testid="aiw-demo-banner">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      {L("بيئة اختبار تجريبية (DEMO ONLY) — لا بيانات حقيقية — مساحة عمل داخلية للفريق الضريبي فقط",
        "DEMO ENVIRONMENT — Internal Tax Specialist Workspace Only — No Client Data")}
    </div>
  );
}
function SyntheticWarn({ L }) {
  return (
    <div className="bg-rose-50 border-2 border-rose-500/40 rounded-lg p-3 text-rose-900 flex items-start gap-3" data-testid="aiw-synthetic-warn">
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
      <div>
        <div className="font-bold text-sm">{L("تنبيه هام: مخرجات اصطناعية تجريبية — لا تُعد رأياً ضريبياً رسمياً", "TEST / SYNTHETIC — NOT A TAX OPINION")}</div>
        <div className="text-[11px] mt-0.5">{L("نص مولّد لمحرك الاختبار الداخلي. يجب مراجعته واعتماده بشرياً قبل أي استخدام.", "Generated by the internal test engine. Must be human-reviewed and approved before any use.")}</div>
      </div>
    </div>
  );
}

const TABS = [
  ["", "لوحة الأخصائي", "Specialist Dashboard", LayoutDashboard],
  ["new", "صياغة جديدة", "New VAT Draft", PenTool],
  ["review", "قائمة المراجعة", "Review Queue", CheckSquare],
  ["approvals", "قائمة الاعتماد", "Approvals Queue", ShieldCheck],
  ["escalations", "طابور التصعيد", "Escalations", AlertOctagon],
];

function Shell({ L, children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const sub = loc.pathname.replace(/^\/app\/ai-workspace\/?/, "").split("/")[0];
  return (
    <div className="space-y-4" data-testid="ai-workspace">
      <DemoBanner L={L} />
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: NAVY }}>
          <PenTool className="h-6 w-6" /> {L("مساحة عمل المختص الضريبي", "Internal Tax Specialist Workspace")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{L("صياغة آراء ضريبة القيمة المضافة المُؤسَّسة — داخلي فقط · VAT فقط · بيئة تجريبية.", "Grounded VAT draft authoring — internal-only · VAT-only · demo environment.")}</p>
      </div>
      <div className="flex gap-1 flex-wrap border-b border-border pb-2" data-testid="aiw-tabs">
        {TABS.map(([p, ar, en, Icon]) => {
          const active = sub === p;
          return (
            <button key={p || "home"} data-testid={`aiw-tab-${p || "dashboard"}`} onClick={() => nav(`/app/ai-workspace/${p}`)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? "text-white border-transparent" : "bg-white text-foreground/70 border-border"}`}
              style={active ? { backgroundColor: NAVY } : {}}>
              <Icon className="h-3.5 w-3.5" /> {L(ar, en)}
            </button>
          );
        })}
      </div>
      {children}
    </div>
  );
}

function Coverage({ cov, L }) {
  if (!cov) return null;
  return (
    <Card data-testid="aiw-coverage">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{L("مؤشر كفاية الأدلة (استرشادي — ليس يقينًا قانونيًا)", "Evidence Coverage (heuristic — NOT legal certainty)")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        {[["coverage_pct", "التغطية", "Coverage", (v) => `${Math.round(v * 100)}%`],
          ["cited_with_article_pct", "باقتباس مادة", "With article", (v) => `${Math.round(v * 100)}%`],
          ["max_similarity", "أعلى تشابه", "Top sim", (v) => v],
          ["min_similarity", "أدنى تشابه", "Min sim", (v) => v]].map(([k, ar, en, f]) => (
          <div key={k} className="rounded-lg border border-border py-2">
            <div className="text-[10px] text-muted-foreground">{L(ar, en)}</div>
            <div className="text-base font-bold font-mono">{f(cov[k] ?? 0)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Dashboard({ L }) {
  const nav = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const load = useCallback(() => kapi().get("/ai/drafts").then(({ data }) => setDrafts(data.drafts || [])).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const counts = Object.keys(SMETA).reduce((a, s) => ({ ...a, [s]: drafts.filter((d) => d.status === s).length }), {});
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="aiw-kpis">
        {Object.keys(SMETA).map((s) => (
          <button key={s} data-testid={`aiw-kpi-${s}`} className="rounded-lg border border-border bg-white px-3 py-2.5 text-right hover:border-foreground/30"
            onClick={() => { const q = s === "technical_review" ? "review" : s === "reviewed" ? "approvals" : s === "escalated" ? "escalations" : ""; nav(`/app/ai-workspace/${q}`); }}>
            <div className="text-[10px] text-muted-foreground truncate">{L(SMETA[s].ar, SMETA[s].en)}</div>
            <div className="text-lg font-bold">{counts[s] || 0}</div>
          </button>
        ))}
      </div>
      <DraftTable L={L} drafts={drafts} />
    </div>
  );
}

function DraftTable({ L, drafts }) {
  const nav = useNavigate();
  if (!drafts.length) return <Card data-testid="aiw-empty"><div className="text-[11px] text-muted-foreground">{L("لا مسودّات.", "No drafts.")}</div></Card>;
  return (
    <Card className="overflow-x-auto p-0" data-testid="aiw-draft-table">
      <table className="w-full text-xs">
        <thead className="bg-secondary/60 text-muted-foreground">
          <tr className="text-right">
            {["السؤال", "الوضع", "التغطية", "الحالة", "النسخة", "الأخصائي"].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{L(h, ["Question", "Mode", "Coverage", "Status", "Ver", "Drafter"][i])}</th>)}
          </tr>
        </thead>
        <tbody>
          {drafts.map((d) => (
            <tr key={d.id} data-testid={`aiw-row-${d.id}`} className="border-t border-border hover:bg-secondary/40 cursor-pointer" onClick={() => nav(`/app/ai-workspace/drafts/${d.id}`)}>
              <td className="px-3 py-2 max-w-[260px] truncate">{d.question}</td>
              <td className="px-3 py-2">{d.mode === "historical" ? L("تاريخي", "Historical") : L("حالي", "Current")}</td>
              <td className="px-3 py-2 font-mono">{Math.round((d.coverage?.coverage_pct || 0) * 100)}%</td>
              <td className="px-3 py-2"><Chip status={d.status} /></td>
              <td className="px-3 py-2 font-mono">v{d.current_version}</td>
              <td className="px-3 py-2 truncate">{d.drafter}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Queue({ L, status, title_ar, title_en }) {
  const [drafts, setDrafts] = useState([]);
  useEffect(() => { kapi().get(`/ai/drafts?status=${status}`).then(({ data }) => setDrafts(data.drafts || [])).catch((e) => toast.error(apiError(e))); }, [status]);
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold" style={{ color: NAVY }}>{L(title_ar, title_en)}</h2>
      <DraftTable L={L} drafts={drafts} />
    </div>
  );
}

function NewDraft({ L }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("current");
  const [asOf, setAsOf] = useState("");
  const [busy, setBusy] = useState(false);
  const PII = /(\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b)|(\b3\d{14}\b)|(\b\d{9,}\b)/;
  const risky = PII.test(q);
  const submit = () => {
    setBusy(true);
    const body = { question: q, category: "VAT", mode };
    if (mode === "historical" && asOf) body.as_of_date = asOf;
    kapi().post("/ai/drafts", body)
      .then(({ data }) => { toast.success(L("تم الإنشاء", "Created")); nav(`/app/ai-workspace/drafts/${data.id}`); })
      .catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  };
  return (
    <div className="max-w-2xl space-y-3">
      <SyntheticWarn L={L} />
      <Card className="space-y-3" data-testid="aiw-new-form">
        <div>
          <label className="text-[11px] font-bold text-muted-foreground">{L("السؤال الضريبي (VAT)", "VAT question")}</label>
          <textarea data-testid="aiw-new-question" rows={4} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={L("مثال: ما نسبة ضريبة القيمة المضافة على تأجير العقار التجاري؟", "e.g. What is the VAT rate on commercial property leasing?")}
            className="mt-1 w-full text-sm rounded border border-border p-2" />
          {risky && (
            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="aiw-pii-warn">
              <Lock className="h-3.5 w-3.5" /> {L("تحذير: قد يحتوي بيانات عميل سرّية (رقم ضريبي/IBAN/حساب). سيُرفض الإرسال حمايةً للبيانات.", "Warning: may contain confidential client data (TIN/IBAN/account). Submission will be blocked (fail-closed).")}
            </div>
          )}
        </div>
        <div className="flex gap-3 flex-wrap">
          <div>
            <label className="text-[11px] font-bold text-muted-foreground block">{L("الفئة", "Category")}</label>
            <select data-testid="aiw-new-category" disabled value="VAT" className="mt-1 text-sm rounded border border-border p-2 bg-secondary/40"><option>VAT</option></select>
          </div>
          <div>
            <label className="text-[11px] font-bold text-muted-foreground block">{L("الوضع الزمني", "Temporal mode")}</label>
            <select data-testid="aiw-new-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1 text-sm rounded border border-border p-2">
              <option value="current">{L("الأنظمة الحالية", "Current rules")}</option>
              <option value="historical">{L("أثر تاريخي", "Historical (as-of)")}</option>
            </select>
          </div>
          {mode === "historical" && (
            <div>
              <label className="text-[11px] font-bold text-muted-foreground block">{L("حتى تاريخ", "As-of date")}</label>
              <input data-testid="aiw-new-asof" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="mt-1 text-sm rounded border border-border p-2" />
            </div>
          )}
        </div>
        <button data-testid="aiw-new-submit" disabled={!q.trim() || risky || busy} onClick={submit}
          className="inline-flex items-center gap-1.5 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: NAVY }}>
          <PenTool className="h-4 w-4" /> {busy ? L("جارٍ…", "Working…") : L("توليد المسودّة", "Generate draft")}
        </button>
      </Card>
    </div>
  );
}

function DraftDetail({ L }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("content");
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [exporting, setExporting] = useState(false);
  const load = useCallback(() => kapi().get(`/ai/drafts/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "versions") kapi().get(`/ai/drafts/${id}/versions`).then(({ data }) => setVersions(data.versions || []));
    if (tab === "comments") kapi().get(`/ai/drafts/${id}/comments`).then(({ data }) => setComments(data.comments || []));
    if (tab === "audit") kapi().get(`/ai/drafts/${id}/audit`).then(({ data }) => setAuditRows(data.audit || []));
  }, [tab, id]);
  if (!d) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  const role = user?.role, uid = user?.id;
  const isDrafter = uid && uid === d.drafter_id;
  const canSubmit = MAKER.includes(role) && ["draft", "revision"].includes(d.status);
  const canReview = REVIEW.includes(role) && !isDrafter && d.status === "technical_review";
  const canApprove = APPROVE.includes(role) && uid !== d.drafter_id && uid !== d.reviewer_id && d.status === "reviewed";
  const canRegen = MAKER.includes(role);
  const act = (path, body) => kapi().post(`/ai/drafts/${id}/${path}`, body || {}).then(() => { toast.success(L("تم", "Done")); load(); setTab("content"); }).catch((e) => toast.error(apiError(e)));
  const doCompare = () => { if (versions.length >= 2) kapi().get(`/ai/drafts/${id}/compare?a=${versions[versions.length - 2].version}&b=${versions[versions.length - 1].version}`).then(({ data }) => setCmp(data)); };
  const addComment = () => kapi().post(`/ai/drafts/${id}/comments`, { text: commentText }).then(() => { setCommentText(""); kapi().get(`/ai/drafts/${id}/comments`).then(({ data }) => setComments(data.comments || [])); });
  const exportMemo = () => {
    setExporting(true);
    kapi().get(`/ai/drafts/${id}/memo.pdf`, { responseType: "blob" })
      .then(({ data }) => {
        const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url; a.download = `vatrdar-advisory-memo-${id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(L("تم تنزيل المذكّرة", "Memorandum downloaded"));
      })
      .catch((e) => toast.error(apiError(e))).finally(() => setExporting(false));
  };

  return (
    <div className="space-y-3" data-testid="aiw-detail">
      <DemoBanner L={L} />
      <SyntheticWarn L={L} />
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-bold text-sm">{d.question}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{d.category} · {d.mode === "historical" ? `${L("تاريخي", "historical")} ${d.as_of_date || ""}` : L("حالي", "current")} · v{d.current_version} · {d.drafter}</div>
          </div>
          <Chip status={d.status} />
        </div>
        {d.prior_approvals_invalidated && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1" data-testid="aiw-invalidated">
            <AlertTriangle className="h-3.5 w-3.5" /> {L("تنبيه: عُدِّلت المسودّة بعد المراجعة — أُبطلت الاعتمادات السابقة وتلزم دورة جديدة.", "Draft revised after review — prior approvals invalidated; new cycle required.")}
          </div>
        )}
        {d.status === "escalated" && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1" data-testid="aiw-escalation">
            <AlertOctagon className="h-3.5 w-3.5" /> {L("مطلوب مراجعة فنية ضريبية", "Technical Tax Review Required")}: {d.escalation_reason || (d.guard?.violations || []).map((v) => v.type).join(", ")}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {canSubmit && <Act tid="submit" icon={Send} label={L("إرسال للمراجعة", "Submit")} onClick={() => act("submit")} />}
          {canReview && <Act tid="review-approve" icon={CheckCircle2} label={L("اعتماد المراجعة", "Approve review")} onClick={() => act("review", { decision: "approve" })} />}
          {canReview && <Act tid="review-revision" icon={RefreshCw} label={L("طلب تعديل", "Request revision")} onClick={() => act("review", { decision: "revision" })} />}
          {canReview && <Act tid="review-reject" icon={XCircle} label={L("رفض", "Reject")} onClick={() => act("review", { decision: "reject" })} />}
          {canApprove && <Act tid="approve" icon={ShieldCheck} label={L("اعتماد نهائي", "Final approve")} onClick={() => act("approve")} />}
          {canRegen && <Act tid="regenerate" icon={RefreshCw} label={L("إعادة الصياغة", "Regenerate")} onClick={() => act("regenerate")} />}
          {d.status === "approved" && (
            <button data-testid="aiw-act-export-pdf" disabled={exporting} onClick={exportMemo}
              className="inline-flex items-center gap-1.5 text-white rounded-full px-3.5 py-1.5 text-[11px] font-semibold disabled:opacity-50"
              style={{ backgroundColor: NAVY }}>
              <Download className="h-3.5 w-3.5" /> {exporting ? L("جارٍ التصدير…", "Exporting…") : L("تصدير المذكّرة الاستشارية (PDF)", "Export Advisory Memorandum (PDF)")}
            </button>
          )}
          {MAKER.includes(role) && isDrafter && d.status === "technical_review" && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary rounded px-2 py-1" data-testid="aiw-sod-badge">
              <Lock className="h-3 w-3" /> {L("فصل المهام: أنت الصائغ (لا تراجع/تعتمد عملك)", "SoD: you are the drafter (cannot review/approve your own)")}
            </span>
          )}
        </div>
      </Card>

      <div className="flex gap-1 flex-wrap" data-testid="aiw-detail-tabs">
        {[["content", "المحتوى", "Content", FileText], ["versions", "النسخ", "Versions", History], ["comments", "التعليقات", "Comments", MessageSquare], ["audit", "التدقيق", "Audit", FileSpreadsheet], ...(d.status === "approved" ? [["evidence", "لقطة الأدلة", "Evidence", ShieldCheck]] : [])].map(([k, ar, en, Icon]) => (
          <button key={k} data-testid={`aiw-dtab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${tab === k ? "text-white border-transparent" : "bg-white text-foreground/70 border-border"}`}
            style={tab === k ? { backgroundColor: NAVY } : {}}><Icon className="h-3.5 w-3.5" /> {L(ar, en)}</button>
        ))}
      </div>

      {tab === "content" && (
        <div className="space-y-3">
          <Coverage cov={d.coverage} L={L} />
          <Card data-testid="aiw-claims">
            {(d.draft_body || []).length === 0 && <div className="text-[11px] text-muted-foreground">{L("لا محتوى — مُصعَّد للمراجعة الفنية.", "No content — escalated for technical review.")}</div>}
            {(d.draft_body || []).map((cl) => {
              const cites = (d.retrieval_snapshot?.citations || []).filter((c) => (cl.citation_ids || []).includes(c.source_id));
              return (
                <div key={cl.claim_id} className="border-b border-border last:border-0 py-2" data-testid={`aiw-claim-${cl.claim_id}`}>
                  <div className="flex items-start gap-2">
                    {cl.is_conclusion && <span className="text-[9px] font-bold bg-navy/10 text-[hsl(217_63%_30%)] rounded px-1.5 py-0.5 shrink-0 mt-0.5">{L("استنتاج", "Conclusion")}</span>}
                    <div className="text-sm">{cl.text_en}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {cites.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-secondary rounded px-1.5 py-0.5 font-mono" data-testid="aiw-citation">
                        {c.authority} · {c.article_code || "—"} · v{c.version} · {c.effective_from || "—"}→{c.effective_to || "—"}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
          {d.guard && (
            <Card data-testid="aiw-guard">
              <div className="text-[10px] font-bold uppercase text-muted-foreground mb-1">{L("حُرّاس التأسيس", "Grounding guards")}</div>
              <div className="text-[11px]">{d.guard.passed ? <span className="text-emerald-700">{L("اجتازت كل الحُرّاس", "All guards passed")}</span> : <span className="text-rose-700">{(d.guard.violations || []).map((v) => v.type).join(" · ")}</span>}</div>
            </Card>
          )}
        </div>
      )}

      {tab === "versions" && (
        <Card data-testid="aiw-versions">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase text-muted-foreground">{L("تأريخ النسخ (غير قابل للتعديل)", "Immutable version history")}</div>
            {versions.length >= 2 && <button data-testid="aiw-compare-btn" onClick={doCompare} className="inline-flex items-center gap-1 text-[11px] border border-border rounded px-2 py-1"><GitCompare className="h-3.5 w-3.5" /> {L("قارن آخر نسختين", "Compare last two")}</button>}
          </div>
          {versions.map((v) => (
            <div key={v.version} className="flex items-center gap-2 text-[11px] border-t border-border py-1.5" data-testid={`aiw-version-${v.version}`}>
              <span className="font-mono font-bold">v{v.version}</span><span className="text-muted-foreground">{v.created_by}</span>
              <span className="text-muted-foreground">{(v.created_at || "").slice(0, 19)}</span>
              <span className="text-[10px] text-muted-foreground">(+{v.diff_from_prev?.added || 0} / -{v.diff_from_prev?.removed || 0} / ~{v.diff_from_prev?.changed || 0})</span>
            </div>
          ))}
          {cmp && (
            <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="aiw-compare">
              {[["a", cmp.a], ["b", cmp.b]].map(([k, v]) => (
                <div key={k} className="border border-border rounded p-2">
                  <div className="text-[10px] font-bold mb-1">v{v.version}</div>
                  {(v.draft_body || []).map((c) => <div key={c.claim_id} className="text-[11px] py-0.5">{c.text_en}</div>)}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "comments" && (
        <Card data-testid="aiw-comments">
          {REVIEW.includes(role) && (
            <div className="flex gap-2 mb-3">
              <input data-testid="aiw-comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder={L("أضف تعليق مراجعة…", "Add reviewer comment…")} className="flex-1 text-xs rounded border border-border p-2" />
              <button data-testid="aiw-comment-add" disabled={!commentText.trim()} onClick={addComment} className="text-[11px] text-white rounded px-3 disabled:opacity-50" style={{ backgroundColor: NAVY }}>{L("إضافة", "Add")}</button>
            </div>
          )}
          {comments.map((c) => (
            <div key={c.id} className="border-t border-border py-1.5 text-[11px] flex items-center justify-between" data-testid={`aiw-comment-${c.id}`}>
              <div><b>{c.reviewer}</b> {c.claim_id ? `· ${c.claim_id}` : ""} — {c.text}</div>
              <span className={`text-[9px] rounded px-1.5 py-0.5 ${c.status === "resolved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{c.status === "resolved" ? L("معالَج", "resolved") : L("مفتوح", "open")}</span>
            </div>
          ))}
          {!comments.length && <div className="text-[11px] text-muted-foreground">{L("لا تعليقات.", "No comments.")}</div>}
        </Card>
      )}

      {tab === "audit" && (
        <Card className="overflow-x-auto" data-testid="aiw-audit">
          <div className="text-[10px] font-bold uppercase text-muted-foreground mb-2">{L("سجل تدقيق غير قابل للتعديل", "Immutable audit trail")}</div>
          <table className="w-full text-[11px]">
            <thead className="text-muted-foreground"><tr className="text-right">{["الحدث", "الفاعل", "الدور", "التاريخ"].map((h, i) => <th key={i} className="px-2 py-1 font-semibold">{L(h, ["Event", "Actor", "Role", "Time"][i])}</th>)}</tr></thead>
            <tbody>{auditRows.map((a) => (<tr key={a.id} className="border-t border-border"><td className="px-2 py-1 font-mono">{a.event}</td><td className="px-2 py-1">{a.actor}</td><td className="px-2 py-1">{a.role}</td><td className="px-2 py-1">{(a.at || "").slice(0, 19)}</td></tr>))}</tbody>
          </table>
        </Card>
      )}

      {tab === "evidence" && d.evidence && (
        <Card data-testid="aiw-evidence">
          <div className="text-[10px] font-bold uppercase text-muted-foreground mb-2">{L("لقطة الأدلة المعتمدة (للقراءة فقط)", "Approved evidence snapshot (read-only)")}</div>
          <div className="text-[11px] space-y-1">
            <div>{L("جُمّدت في", "Frozen at")}: <span className="font-mono">{d.evidence.frozen_at}</span></div>
            <div>{L("النموذج", "Model")}: <span className="font-mono">{d.evidence.model}</span> · v{d.evidence.version}</div>
            <div>{L("الاقتباسات", "Citations")}: {(d.evidence.retrieval_snapshot?.citations || []).length}</div>
          </div>
        </Card>
      )}
    </div>
  );
}
function Act({ label, icon: Icon, onClick, tid }) {
  return <button data-testid={`aiw-act-${tid}`} onClick={onClick} className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2.5 py-1.5 hover:bg-secondary"><Icon className="h-3.5 w-3.5" /> {label}</button>;
}

export default function AIWorkspace() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const L = (ar, en) => (lang === "ar" ? ar : en);
  if (user && CLIENT.includes(user.role)) return <Navigate to="/app" replace />;
  return (
    <Shell L={L}>
      <Routes>
        <Route index element={<Dashboard L={L} />} />
        <Route path="new" element={<NewDraft L={L} />} />
        <Route path="review" element={<Queue L={L} status="technical_review" title_ar="قائمة المراجعة الفنية (Checker)" title_en="Technical Review Queue" />} />
        <Route path="approvals" element={<Queue L={L} status="reviewed" title_ar="قائمة الاعتماد النهائي (Approver)" title_en="Final Approvals Queue" />} />
        <Route path="escalations" element={<Queue L={L} status="escalated" title_ar="طابور التصعيد الفني" title_en="Technical Escalation Queue" />} />
        <Route path="drafts/:id" element={<DraftDetail L={L} />} />
      </Routes>
    </Shell>
  );
}
